import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { AlertMessage, useCheckoutContext } from '@zooplus/checkout-state';
import { AlertCircle } from '@zooplus/icons';
import { Input, P1, P2, PrimaryButton, SecondaryButton } from '@zooplus/ui-components';

import FeedbackMessage from '../../common/notifications/feedback-message';
import { SetCompleteProps } from '../preview-containers-section/preview-containers-section';

import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import { useEmailState } from '@hooks/use-email-state';
import { useLoginRegisterLinkHandler } from '@hooks/use-login-register-link-handler';
import { useTranslation } from '@hooks/use-translations';
import {
  CHECKOUT_STATE_PROJECT_ID,
  MESSAGE_PLACEMENT,
  MESSAGE_CODE,
  CUSTOMER_TYPE,
  TARGET_ACTION_ID,
  TARGET_COMPONENT_NAME,
  TRACKING_DATA_EVENT,
} from '@utilities/constants';
import { HTMLParser } from '@utilities/html-parser';
import { isArrayNotEmpty } from '@utilities/utils';

import styles from './customer-information.module.scss';
import ReadOnlyEmailSection from './read-only-email-section';
import SubscriptionAlert from './subscription-alert';

type CustomerInformationProps = {
  isContainerActive?: boolean;
  setComplete?: SetCompleteProps;
};

const EVENT = {
  DISCARD: 'discard',
  SAVE_AND_CONTINUE: 'save-and-continue',
  CONTINUE_TO_NEXT_STEP: 'continue-to-next-step',
} as const;

type LoaderStates = Record<(typeof EVENT)[keyof typeof EVENT], boolean>;

const CustomerInformation = ({ isContainerActive, setComplete }: CustomerInformationProps) => {
  const { isLoading, checkoutState, createBasicGuestAccount, updateGuestAccountEmail } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const { trackEventUsingTrackerListener } = usePageTracking();
  const hasClickedButton = useRef(false);
  const emailContainerRef = useRef<HTMLDivElement>(null);

  const [shouldDiscardChanges, setShouldDiscardChanges] = useState(false);
  const [buttonLoading, setButtonLoading] = useState<LoaderStates>({
    [EVENT.DISCARD]: false,
    [EVENT.SAVE_AND_CONTINUE]: false,
    [EVENT.CONTINUE_TO_NEXT_STEP]: false,
  });
  const { emailState, updateEmailState, handleEmailChange, validateEmailState } = useEmailState();

  const { customer, stateMessages, cart, stateAttributes } = checkoutState ?? {};
  const { customerId, customerType, email: customerEmail } = customer ?? {};
  const customerMessages = stateMessages?.customerMessages as AlertMessage[];
  const isSubscriptionApplied = useMemo(
    () => cart?.articles?.some((article) => article.subscriptionApplied),
    [cart?.articles],
  );
  const keycloakData = stateAttributes?.keycloakData;

  useEffect(() => {
    if (customerEmail) {
      updateEmailState({ value: customerEmail });
    }
  }, []);

  useEffect(() => {
    if (customerEmail && isContainerActive) {
      updateEmailState({
        value: customerEmail,
        isEmpty: false,
        isValid: true,
        isExisting: false,
      });
    }
  }, [isContainerActive, customerEmail]);

  useEffect(() => {
    if (
      !isLoading &&
      customerId &&
      !isArrayNotEmpty(customerMessages) &&
      customerType === CUSTOMER_TYPE.GUEST_CUSTOMER &&
      !emailState.isSubmittedSimilar
    ) {
      setComplete?.(true);
    }

    updateEmailState({ isSubmittedSimilar: false });
    setShouldDiscardChanges(false);
  }, [isLoading, customerId, customerType, emailState.isSubmittedSimilar, shouldDiscardChanges]);

  useEffect(() => {
    if (isArrayNotEmpty(customerMessages)) {
      const message = customerMessages.find((message) =>
        [
          MESSAGE_CODE.CHECKOUT_PREVIEW_CUSTOMER_EXISTING_EMAIL,
          MESSAGE_CODE.CHECKOUT_PREVIEW_CUSTOMER_INVALID_EMAIL,
        ].includes(message.code),
      );

      updateEmailState({
        isExisting: message?.code === MESSAGE_CODE.CHECKOUT_PREVIEW_CUSTOMER_EXISTING_EMAIL,
        isValid: message?.code !== MESSAGE_CODE.CHECKOUT_PREVIEW_CUSTOMER_INVALID_EMAIL,
      });
      hasClickedButton.current = false;
      setButtonLoading({
        [EVENT.DISCARD]: false,
        [EVENT.SAVE_AND_CONTINUE]: false,
        [EVENT.CONTINUE_TO_NEXT_STEP]: false,
      });
    }
  }, [customerMessages]);

  useEffect(() => {
    if (!customerId && isSubscriptionApplied) {
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.IMPRESSION,
        TARGET_ACTION_ID.ACCOUNT_BENEFITS,
        TARGET_COMPONENT_NAME.CUSTOMER_INFORMATION,
      );
    }
  }, [customerId, isSubscriptionApplied, trackEventUsingTrackerListener]);

  const handleLoaderState = useCallback((buttonId: string, value: boolean) => {
    setButtonLoading((prevState) => ({ ...prevState, [buttonId]: value }));
  }, []);

  const handleDiscardAction = useCallback(() => {
    if (customerEmail && isArrayNotEmpty(customerMessages)) {
      updateGuestAccountEmail?.({ email: customerEmail }).then(() => {
        setShouldDiscardChanges(true);
        hasClickedButton.current = false;
        handleLoaderState(EVENT.DISCARD, false);
      });
      return;
    }
    setShouldDiscardChanges(true);
    hasClickedButton.current = false;
    handleLoaderState(EVENT.DISCARD, false);
  }, [customerEmail, customerMessages, updateGuestAccountEmail, handleLoaderState]);

  const handleSaveAction = useCallback(() => {
    const { value: email } = emailState;
    const updateEmail = customerId ? updateGuestAccountEmail : createBasicGuestAccount;

    updateEmail?.({ email }).then(() => {
      hasClickedButton.current = false;
      handleLoaderState(EVENT.SAVE_AND_CONTINUE, false);
    });
  }, [emailState, customerId, updateGuestAccountEmail, createBasicGuestAccount, handleLoaderState]);

  const handleButtonClick = useCallback(
    (event?: string) => {
      if (hasClickedButton.current) return;

      if (event !== EVENT.DISCARD) {
        const { value: email } = emailState;
        const isEmailBlank = email === '';
        const isSubmittedEmailSimilar = email === customerEmail;

        const isValid = isEmailBlank ? true : validateEmailState(email);

        updateEmailState({
          isEmpty: isEmailBlank,
          isSubmittedSimilar: isSubmittedEmailSimilar,
          isValid,
        });

        if (isEmailBlank || isSubmittedEmailSimilar || !isValid || emailState.isExisting) {
          return;
        }
      }

      hasClickedButton.current = true;
      handleLoaderState(event ?? '', true);

      if (event === EVENT.DISCARD) {
        handleDiscardAction();
      } else {
        handleSaveAction();
      }
    },
    [handleLoaderState, handleDiscardAction, handleSaveAction, emailState, customerEmail],
  );

  const handleCustomerClick = useCallback(
    (targetActionId: TARGET_ACTION_ID) => {
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.CLICK,
        targetActionId,
        TARGET_COMPONENT_NAME.CUSTOMER_INFORMATION,
      );
    },
    [trackEventUsingTrackerListener],
  );

  useLoginRegisterLinkHandler(emailContainerRef, handleCustomerClick, keycloakData);

  const renderErrorMessage = (message: string, errorType: string) => (
    <span className={styles.errorWrapper} data-testid={errorType}>
      <AlertCircle width={20} height={20} />
      <P2 className={styles.emailErrorText}>{message}</P2>
    </span>
  );

  const renderEmailInput = () => {
    const hasError = emailState.isEmpty || !emailState.isValid;
    return (
      <Input
        data-testid="email-input-field"
        hasError={hasError}
        inputProps={{ value: emailState.value }}
        onChange={handleEmailChange}
      />
    );
  };

  const renderActionButtons = () =>
    customerId ? (
      <div className={styles.saveOrDiscardButtonWrapper}>
        <SecondaryButton
          isLoading={buttonLoading[EVENT.DISCARD]}
          onClick={() => handleButtonClick(EVENT.DISCARD)}
          data-testid="discard-changes-button">
          {t('checkout.preview.discard.changes')}
        </SecondaryButton>
        <PrimaryButton
          isLoading={buttonLoading[EVENT.SAVE_AND_CONTINUE]}
          onClick={() => handleButtonClick(EVENT.SAVE_AND_CONTINUE)}
          data-testid="save-and-update-button">
          {t('checkout.preview.save.and.update')}
        </PrimaryButton>
      </div>
    ) : (
      <PrimaryButton
        data-testid="continue-to-next-step-button"
        isLoading={buttonLoading[EVENT.CONTINUE_TO_NEXT_STEP]}
        onClick={() => handleButtonClick(EVENT.CONTINUE_TO_NEXT_STEP)}
        className={styles.continueButton}>
        {t('checkout.preview.continueTo.deliveryAndInvoice')}
      </PrimaryButton>
    );

  const renderEmailInputContainer = () => (
    <>
      <P1 bold className={styles.title} data-testid="email-address-title">
        {t('checkout.preview.customer.email.address')}
      </P1>
      {renderEmailInput()}
      {emailState.isEmpty &&
        renderErrorMessage(t('checkout.preview.customer.email.blank'), 'email-blank-error')}
      {!emailState.isValid &&
        renderErrorMessage(t('checkout.preview.customer.invalid.email'), 'email-invalid-error')}
      {!customerId && (
        <HTMLParser
          tagName={P2}
          data-testid="support-text"
          customStyle={styles.supportText}
          content={t('checkout.preview.supportText')}
        />
      )}
      {renderActionButtons()}
    </>
  );

  return (
    <div
      ref={emailContainerRef}
      className={styles.emailAddressWrapper}
      data-testid="email-address-section">
      {!customerId && isSubscriptionApplied && <SubscriptionAlert />}
      {isContainerActive && (
        <FeedbackMessage
          customStyle={styles.feedbackAlert}
          placement={MESSAGE_PLACEMENT.PREVIEW_CUSTOMER_INFORMATION_HEADER}
          isEmailExisting={emailState.isExisting}
        />
      )}
      {!isContainerActive ? (
        <ReadOnlyEmailSection email={customerEmail} />
      ) : (
        renderEmailInputContainer()
      )}
    </div>
  );
};

export default CustomerInformation;
